import type { LimuloMatch, LimuloTournament } from '@/types/limulo';

import { matchToOpenOf } from './code';
import { matchStateOf } from './match';
import { limuloMatchUrl, limuloTournamentUrl } from './routes';

/**
 * DOVE PORTA `t.me/<bot>?startapp=inc_<tournamentId>` — la scelta, pura (I-356, ADR-0194).
 *
 * Il link dice SOLO il torneo (`lib/startParam.ts`): quale INCONTRO aprire lo dicono i dati dell'utente, letti dalla schermata
 * d'atterraggio (`routes/IncontroLanding.tsx`), e questo file e' la decisione, senza rete e senza React perche' un test la
 * esegua e un mutante la possa rompere.
 *
 * ⚠️ Il `tournamentId` che arriva dal link e' un INPUT NON FIDATO. Decide una sola cosa: se chiedere gli incontri (`tornoFraIMiei`).
 * Il `matchId` viene SEMPRE dai miei incontri, mai dal link (che non lo porta: `<tid>/<step>/<lane>/r<n>/<pty>+<pty>`, ~54 caratteri).
 */

/** Dove si va: la scheda dell'incontro aperto, o quella del torneo. */
export type DestinazioneDaLink =
  | { kind: 'incontro'; matchId: string; url: string }
  | { kind: 'torneo'; url: string };

/**
 * Il torneo del link e' fra i MIEI (`GET /v1/tournaments/mine`)? Solo allora si chiedono gli incontri.
 *
 * Si confronta col `tournamentId` di business (`tournamentId || id`, come fanno gia' `AzionePersistente` e la scheda torneo):
 * l'`id` e' un ObjectId e non puo' coincidere con un tid del link, ma la regola dei vicini resta una.
 * `null`/`undefined` (lettura non arrivata) e' «non lo so» e per questa funzione e' `false`: chi chiama distingue «non e' arrivata»
 * da «e' arrivata e non c'e'» PRIMA, perche' la prima non decide niente (v. la landing).
 */
export function tornoFraIMiei(
  tournamentId: string,
  miei: readonly Pick<LimuloTournament, 'id' | 'tournamentId'>[] | null | undefined,
): boolean {
  if (tournamentId === '' || !Array.isArray(miei)) return false;
  return miei.some((t) => (t.tournamentId || t.id) === tournamentId);
}

/**
 * La destinazione.
 *
 * · il torneo NON e' fra i miei (non iscritto, o link a un torneo qualunque) → la scheda del torneo, «come un URL qualunque»: la
 *   pagina del torneo gestisce gia' il non-trovato. ⛔ Gli incontri non si chiedono nemmeno (vedi `tornoFraIMiei`).
 * · e' fra i miei e ho un incontro DA GIOCARE (`matchToOpenOf`: `pending` · `proposed` · `disputed`, lo stesso criterio della
 *   «partita trovata» delle code) → la scheda di QUELL'incontro.
 * · e' fra i miei ma non ne ho di aperti (gia' giocato, round passato, torneo concluso) → la scheda del torneo.
 *
 * `GET …/matches` e' «i miei» per costruzione del motore (v. `matchToOpenOf`): la lista non contiene incontri altrui, quindi
 * il primo aperto e' mio.
 */
export function destinazioneIncontroDaLink(args: {
  tournamentId: string;
  fraIMiei: boolean;
  incontri: readonly LimuloMatch[] | null | undefined;
}): DestinazioneDaLink {
  const { tournamentId, fraIMiei, incontri } = args;
  if (!fraIMiei) return { kind: 'torneo', url: limuloTournamentUrl(tournamentId) };
  const aperto = matchToOpenOf(incontri, matchStateOf);
  return aperto
    ? { kind: 'incontro', matchId: aperto.matchId, url: limuloMatchUrl(tournamentId, aperto.matchId) }
    : { kind: 'torneo', url: limuloTournamentUrl(tournamentId) };
}
